// @flow
import React from 'react';

import { Map as ImmutableMap } from 'immutable';

import { doAccountRediscovery, doProvideCredentialsAndRefresh } from 'components/Dialogs/AccountDiscovery/actions';

import { refreshAccounts } from 'data/accounts/actions';
import { doReAuthenticate, doUpdateCredentials, doUpdateFi } from 'data/institutionLogins/actions';
import type { InstitutionLogin } from 'data/institutionLogins/types';

import isAcme from 'isAcme';

// --- User facing error messaging ---
// text = Short text displayed to the user.
//    On account settings page it is shown next to error icon.
//    In dialogs it is the title.
// helpText = Long text displayed to the user.
//    On account settings page it is shown if they click dropdown arrow.
//    In dialogs it is subheading (below title).
// userAction = User action text displayed to the user under under 'text' field.
//    Rarely used, but used on account settings page below 'text' field.
// code = Error code returned from server.
// actionLabel = Button label.
// action = Event that happens when user clicks button.

const QcsAggregationErrorMap = {
  INVALID_CREDENTIALS: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => {
    // 'ccscrape.103',
    if (institutionLogin.ewcLive) {
      return ({
        error: 'INVALID_CREDENTIALS',
        text: 'This financial institution needs your attention',
        helpText: `To connect to ${institutionLogin.name}, you must re-enter your credentials. If that does not work, try entering them on the website at ${institutionLogin.name} to ensure they are correct.`,
        status: aggStatus,
        statusCode: aggStatusCode,
        code: 'ccscrape.103',
        actionLabel: 'Enter Credentials',
        action: doProvideCredentialsAndRefresh(institutionLogin),
        institutionLogin,
      });
    }
    return ({
      error: 'INVALID_CREDENTIALS',
      text: 'This financial institution needs your attention',
      helpText: `To connect to ${institutionLogin.name}, you must re-enter your credentials. If that does not work, try entering them on the website at ${institutionLogin.name} to ensure they are correct.`,
      status: aggStatus,
      statusCode: aggStatusCode,
      code: 'ccscrape.103',
      actionLabel: 'Enter Credentials',
      action: doUpdateCredentials(institutionLogin),
      institutionLogin,
    });
  },
  ACCOUNT_NOT_FOUND: (institutionLogin, aggStatus, aggStatusCode, featureFlags) => {
    if (featureFlags.get('aggErrorHandleDeadAccounts')) {
      const content = (
        <div>
          This usually happens if an account nickname or number has changed at your bank.
          To fix you must reconnect your account.
        </div>
      );
      return ({
        // 'ccscrape.324',
        error: 'ACCOUNT_NOT_FOUND',
        text: 'This financial institution needs your attention',
        helpText: content,
        status: aggStatus,
        statusCode: aggStatusCode,
        code: 'ccscrape.324',
        actionLabel: 'Reconnect Account',
        action: doAccountRediscovery(institutionLogin),
        showConfirmationDialog: true,
        institutionLogin,
      });
    }
    const content = (
      <div>
        This usually happens if an account nickname or number changed at your bank.
        To fix you must deactivate and reactivate your account on your desktop. &nbsp;
        <a
          href="https://www.quicken.com/support/troubleshooting-online-banking-issues-quicken-mobile-and-quicken-web"
          target="_blank"
        >
          Click here for instructions
        </a>
        .
      </div>
    );
    return ({
      // 'ccscrape.324',
      error: 'ACCOUNT_NOT_FOUND',
      text: 'This financial institution needs your attention',
      helpText: content,
      status: aggStatus,
      statusCode: aggStatusCode,
      code: 'ccscrape.324',
      actionLabel: 'Try Again',
      action: refreshAccounts({institutionLoginId: institutionLogin.id}),
      showConfirmationDialog: true,
      institutionLogin,
    });
  },
  ACCOUNT_MISMATCH: (institutionLogin, aggStatus, aggStatusCode, featureFlags) => {
    if (featureFlags.get('aggErrorHandleDeadAccounts')) {
      const content = (
        <div>
          This usually happens if an account nickname or number has changed at your bank.
          To fix you must reconnect your account.
        </div>
      );
      return ({
        // 'ccscrape.106',
        error: 'ACCOUNT_MISMATCH',
        text: 'This financial institution needs your attention',
        helpText: content,
        status: aggStatus,
        statusCode: aggStatusCode,
        code: 'ccscrape.106',
        actionLabel: 'Reconnect Account',
        action: doAccountRediscovery(institutionLogin),
        showConfirmationDialog: true,
        institutionLogin,
      });
    }
    const content = (
      <div>
        This usually happens if an account name or number has changed at your bank.
        To fix you must deactivate and reactivate your account in your desktop. &nbsp;
        <a href="https://www.quicken.com/support/troubleshooting-online-banking-issues-quicken-mobile-and-quicken-web" target="_blank">
          Click here for instructions
        </a>
        .
      </div>
    );
    return ({
      // 'ccscrape.106',
      error: 'ACCOUNT_MISMATCH',
      text: 'This financial institution needs your attention',
      helpText: content,
      status: aggStatus,
      statusCode: aggStatusCode,
      code: 'ccscrape.106',
      actionLabel: 'Try Again',
      action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
      showConfirmationDialog: true,
      institutionLogin,
    });
  },
  FI_WEBSITE_UNAVAILABLE: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.105',
    error: 'FI_WEBSITE_UNAVAILABLE',
    errorCode: 'ccscrape.105',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time. Please try again later.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.105',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  FI_TIMEOUT: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.102',
    error: 'FI_TIMEOUT',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time. Please try again later.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.102',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // CP_SCRIPT_ERROR (ccscrape.181) - Unspecified OFX Error
  END_USER_ACTION_REQUIRED_AT_FI: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.108',
    error: 'END_USER_ACTION_REQUIRED_AT_FI',
    text: `Please sign in to your account at the website for ${institutionLogin.name} to complete an action then try again.`,
    userAction: `Please sign in to your account at the website for ${institutionLogin.name} to complete an action then try again.`,
    helpText: `To connect to your account, you must complete an action at the website for ${institutionLogin.name}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.108',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // CP_SCRIPT_ERROR (ccscrape.180) - Invalid OFX File
  // CP_SCRIPT_ERROR (ccscrape.118) - Missing required symbol in symbol table
  // CP_SCRIPT_ERROR (ccscrape.101) - General Script Error
  // CP_SCRIPT_ERROR (ccwebbrwser.519) - Session Failed
  // CP_SCRIPT_ERROR (ccscrape.163) - Account keys have changed
  PASSWORD_CHANGED_REQUIRED_AT_FI: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.109',
    error: 'PASSWORD_CHANGED_REQUIRED_AT_FI',
    text: `Please change your password at the website for ${institutionLogin.name} then try again.`,
    userAction: `Please change your password at the website for ${institutionLogin.name} then try again.`,
    helpText: `To connect to your account, you must change your password at the website for ${institutionLogin.name}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.109',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // CP_SCRIPT_ERROR (cccapture.307) - Communication error between components
  UNSUPPORTED_MFA_PROTOCOL: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.192',
    error: 'UNSUPPORTED_MFA_PROTOCOL',
    text: `Please change your multi-factor authentication method at the website for ${institutionLogin.name} then try again.`,
    userAction: `Please change your multi-factor authentication method at the website for ${institutionLogin.name} then try again.`,
    helpText: `The current multi-factor authentication method at ${institutionLogin.name} is not supported. To connect to your account, you must change the multi-factor authentication method at ${institutionLogin.name}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.192',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // QCS_SERVER_ERROR (authorization.domain.tp_account_id)
  // QCS_SERVER_ERROR (client.connect) - Mostly Gateway Timeout
  // QCS_SERVER_ERROR (authentication.user.credentials)
  // CP_SCRIPT_ERROR (ccwebbrowser.530) - Unknown (handshake failure?)
  MFA_FAILED: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.187',
    error: 'MFA_FAILED',
    text: 'This financial institution needs your attention',
    helpText: `A multi-factor authentication is required to connect to your account at ${institutionLogin.name}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.187',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  MFA_REQUIRED_ACTION: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // todo: need error code
    error: 'MFA_REQUIRED_ACTION',
    text: 'This financial institution needs your attention',
    helpText: `A multi-factor authentication is required to connect to your account at ${institutionLogin.name}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.185',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  CP_SESSION_TIMEOUT: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'cccapture.331',
    error: 'CP_SESSION_TIMEOUT',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'cccapture.331',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // CP_SERVER_ERROR (ccclient.203) - CustomerCentral Client failed to receive a response
  FI_BLOCKING: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.155',
    error: 'CP_SERVER_ERROR',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.155',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // QCS_SERVER_EROR (client.response.xml.validation) - Stack trace from FDS Server
  // CP_SCRIPT_ERROR (ccscrape.100) -
  // CP_SCRIPT_ERROR (ccscrape.124) - Invalid percent value
  // QCS_SERVER_ERROR (client.request.xml.validation)
  // CP_SERVER_ERROR (ccscrape.199) - Unknown
  // CP_SCRIPT_ERROR (ccscrape.175) - Missing required return data
  // CP_SCRIPT_ERROR (ccscrape.141) - Form not found
  // CP_SCRIPT_ERROR (ccscrape.176) - A bill was not found at the site
  // CP_SERVER_ERROR (discover.no.account.found) -
  // QCS_SERVER_ERROR (ccscrape.107) - Incorrect FI selected to add a Particular type of account.
  CREDENTIALS_REQUIRED_ACTION: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'CREDENTIALS_REQUIRED_ACTION',
    text: 'This financial institution needs your attention',
    helpText: `To connect to ${institutionLogin.name}, you must enter your credentials.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'api.database.accountkey.notfound',
    actionLabel: 'Enter Credentials',
    action: doProvideCredentialsAndRefresh(institutionLogin),
    institutionLogin,
  }),
  CUSTOMER_ALREADY_LOGGED_IN_AT_FI: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.179',
    error: 'CUSTOMER_ALREADY_LOGGED_IN_AT_FI',
    text: `Please log out of your account at the website for ${institutionLogin.name} then try again.`,
    userAction: `Please log out of your account at the website for ${institutionLogin.name} then try again.`,
    helpText: `You must log out of your account at ${institutionLogin.name} to connect to your account in ${isAcme ? 'Simplifi' : 'Quicken'}.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.179',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  // CP_SCRIPT_ERROR (ccwebbrowser.580) - Connection Failed
  CP_ACCOUNT_NOT_FOUND: (institutionLogin, aggStatus, aggStatusCode, featureFlags) => {
    if (featureFlags.get('aggErrorHandleDeadAccounts')) {
      const content = (
        <div>
          To restore connectivity to your account, you must reconnect to your bank.
        </div>
      );
      return ({
        // 'api.database.accountkey.notfound',
        error: 'CP_ACCOUNT_NOT_FOUND',
        text: 'This financial institution needs your attention',
        helpText: content,
        status: aggStatus,
        statusCode: aggStatusCode,
        code: 'api.database.accountkey.notfound',
        actionLabel: 'Reconnect Account',
        action: doAccountRediscovery(institutionLogin),
        showConfirmationDialog: true,
        institutionLogin,
      });
    }
    const content = (
      <div>
        To restore connectivity to your account, deactivate and reactivate your account in your Quicken desktop. Click &nbsp;
        <a href="https://www.quicken.com/support/troubleshooting-online-banking-issues-quicken-mobile-and-quicken-web" target="_blank">
          here
        </a>
        &nbsp; for instructions. <br />
        <br />
        If you see this error only on mobile and web but not on desktop, try disabling sync for this account in your Quicken desktop
        and then immediately re-enabling. &nbsp;
        <a href="https://www.quicken.com/support/troubleshooting-online-banking-issues-quicken-mobile-and-quicken-web" target="_blank">
          Click here for instructions
        </a>
        .<br />
      </div>
    );
    return ({
      // 'api.database.accountkey.notfound',
      error: 'CP_ACCOUNT_NOT_FOUND',
      text: 'This financial institution needs your attention',
      userAction: content,
      helpText: content,
      status: aggStatus,
      statusCode: aggStatusCode,
      code: 'api.database.accountkey.notfound',
      actionLabel: 'Try Again',
      action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
      showConfirmationDialog: true,
      institutionLogin,
    });
  },
  // CP_SCRIPT_ERROR (ccwebbrowser.594) - Request Error
  // CP_SCRIPT_ERROR (ccscrape.189) - Script not written yet
  // CP_SCRIPT_ERROR (ccscrape.104) - Parse Error
  // CP_SCRIPT_ERROR (ccscrape.138) - No current table to use
  // CP_SERVER_ERROR (cccapture.302) - A required value is missing from the DB
  // CP_SERVER_ERROR (cccapture.334) - Unknown
  // CP_SCRIPT_ERROR (ccscrape.183) - No question available to add answer choices to.
  // CP_SCRIPT_ERROR (ccwebbrowser.503) - Invalid Request
  // CP_SCRIPT_ERROR (ccwebbrowser.520) - URL Invalid
  // CP_SCRIPT_ERROR (ccwebbrowser.531) - SSL Certificate Exception
  // CP_SCRIPT_ERROR (cccapture.195) - Web service changed
  // CP_SCRIPT_ERROR (ccscrape.119) - Invalid TekPortal data
  // QCS_SERVER_ERROR (api.request.value) -
  // CP_SCRIPT_ERROR (ccscrape.151) - No more previous page
  // CP_SERVER_ERROR (cccapture.332) - Unknown
  // CP_SERVER_ERROR (cccapture.333) - Unknown
  // CP_SCRIPT_ERROR (cccapture.193) - Call stack error
  // CP_SCRIPT_ERROR (ccscrape.156) - Form input field not found
  // CP_SCRIPT_ERROR (ccscrale.123) - Invalid dollar amount
  // CP_SCRIPT_ERROR (ccscrale.127) - String not found
  ACCOUNT_MOVED: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // 'ccscrape.162',
    error: 'ACCOUNT_MOVED',
    text: 'This financial institution needs your attention',
    helpText: 'TBD',
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.162',
    actionLabel: 'Try Again',
    // todo: What do we do here? Deactivate & Reactivate to New FI
    action: null,
    institutionLogin,
  }),
  PAUTH_FLOW_REQUIRED: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'PAUTH_FLOW_REQUIRED',
    text: 'This financial institution needs your attention.',
    helpText: 'To reconnect to your account, do a one step update in your Quicken desktop and follow any instructions to restore connectivity.',
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'cccapture.351',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  PAUTH_INVALID_TOKEN: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'PAUTH_INVALID_TOKEN',
    text: 'This financial institution needs your attention',
    helpText: `${institutionLogin.name} requires you to re-authenticate`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'cccapture.350',
    actionLabel: 'Re-Authenticate',
    action: doReAuthenticate(institutionLogin),
    institutionLogin,
  }),
  PAUTH_SLT_ERROR: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'PAUTH_SLT_ERROR',
    text: 'This financial institution needs your attention',
    helpText: `${institutionLogin.name} requires you to re-authenticate`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'api.partnerauth.error',
    actionLabel: 'Re-Authenticate',
    action: doReAuthenticate(institutionLogin),
    institutionLogin,
  }),
  INCORRECT_FI: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'INCORRECT_FI',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to setup your accounts at '${institutionLogin.name}'. Please try selecting a different Capital One.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'ccscrape.107',
    actionLabel: 'Try Again',
    action: doUpdateFi(institutionLogin),
    institutionLogin,
  }),
  REAUTHENTICATION_REQUIRED: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    error: 'REAUTHENTICATION_REQUIRED',
    text: 'This financial institution needs your attention',
    helpText: `${institutionLogin.name} requires you to re-authenticate`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 're-authenticate',
    actionLabel: 'Re-Authenticate',
    action: doReAuthenticate(institutionLogin),
    institutionLogin,
  }),
  // CP_SCRIPT_ERROR (cccapture.196) - Web service response error
  CP_SCRIPT_ERROR: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => {
    if (aggStatusCode === 'ccscrape.181') {
      if (institutionLogin.brandingId === '10898') {
        return ({
          error: 'END_USER_ACTION_REQUIRED_AT_FI',
          text: `Please sign into your account at Chase and confirm that ${isAcme ? 'Simplifi' : 'Quicken'} can access your accounts`,
          userAction: `Please sign into your account at the Chase website to confirm that ${isAcme ? 'Simplifi' : 'Quicken'} can access your accounts`,
          helpText: 'After signing in to the Chase website, click on the menu button in the upper left corner of the website. Choose Secure Messages. Click most recent message with subject of "Action Required for Quicken" and follow the steps. Once you confirm the access, then return here and click the Try Again button.',
          status: aggStatus,
          statusCode: aggStatusCode,
          code: 'ccscrape.181',
          actionLabel: 'Try Again',
          action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
          showConfirmationDialog: true,
          institutionLogin,
        });
      }
    }
    return ({
      // han'les multiple errors
      error: 'CP_SCRIPT_ERROR',
      text: 'This financial institution needs your attention',
      helpText: `We are unable to connect to ${institutionLogin.name} at this time.`,
      status: aggStatus,
      statusCode: aggStatusCode,
      code: 'cp-error',
      actionLabel: 'Try Again',
      action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
      showConfirmationDialog: true,
      institutionLogin,
    });
  },
  CP_SERVER_ERROR: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // todo: need error code
    error: 'CP_SERVER_ERROR',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: 'cp-error',
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
  GENERIC: (institutionLogin, aggStatus, aggStatusCode, _featureFlags) => ({
    // handles generic / unknown errors
    error: 'GENERIC',
    text: 'This financial institution needs your attention',
    helpText: `We are unable to connect to ${institutionLogin.name} at this time.`,
    status: aggStatus,
    statusCode: aggStatusCode,
    code: null,
    actionLabel: 'Try Again',
    action: refreshAccounts({ institutionLoginId: institutionLogin.id }),
    showConfirmationDialog: true,
    institutionLogin,
  }),
};

// CACHE_TIMEOUT(1350, "Operation took too long, the QCS cache has timed-out.", true),
// CP_USER_CREDENTIALS_ARE_INVALID(1475, "The user credentials are invalid for content provider.", true),
// FI_WEBSITE_IN_SCHEDULED_MAINTANANCE(1900, "Website unavailable on a particular time period.", true),
// MFA_REQUIRED_ACTION(2200, "User action is required to bypass multi-factor authentication.", true),
// NO_ACCOUNTS_FOUND(2250, "No accounts found.", true),
// NO_ACCOUNTS_REFRESHED(2275, "No accounts refreshed.", true),
// STATUS_TERMINATION_TIMEOUT(2350, "Status took too long to change to a terminated status", true),
// UNSUPPORTED_MFA_PROTOCOL(2400, "Unsupported multi-factor authentication protocol", true),
//
// CP_SERVER_ERROR(3000, "Content-Provider Server Error", true),
// CP_SCRIPT_ERROR(3001, "Content-Provider Script Error", true),
//
// QCS_SERVER_ERROR(5000, "QCS Server Error", true),


export const getQcsAggregationError = (
  institutionLogin: InstitutionLogin,
  aggStatus: string,
  aggStatusCode: ?string,
  featureFlags: ImmutableMap<string, any>
) =>
{
  // should QCS do the following?
  if (aggStatus === 'QUEUED_MFA_CHALLENGE' || aggStatus === 'MFA_CHALLENGE_READY_FOR_USER'
  ) {
    aggStatus = 'MFA_REQUIRED_ACTION';  // eslint-disable-line no-param-reassign
  }

  const errorLambda = QcsAggregationErrorMap[aggStatus] || QcsAggregationErrorMap.GENERIC;
  return errorLambda(institutionLogin, aggStatus, aggStatusCode, featureFlags);
};

